import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { HeaderComponent } from '../../../shared/header/header.component';
import { ArquivoGerado, GrupoRelatorios, RelatorioAgenda, StatusJob } from '../../../../interfaces/relatorios-agenda.dto';
import { LocalStorageService } from '../../../../services/local-storage/local-storage.service';
import { RelatoriosAgendaService } from '../../../../services/relatorios/relatorios-agenda.service';
import { abrirPdf, ehPdf, salvarBlob } from '../../../../utils/baixar-arquivo.util';
import { agruparRelatorios } from '../../../../utils/relatorio-filtros.util';
import {
  RelatorioRecente, chaveRecentes, lerRecentes, registrarRecente, rotuloQuando, valoresSeguros,
} from '../../../../utils/relatorios-recentes.util';
import { RelatorioFiltrosComponent } from '../relatorio-filtros/relatorio-filtros.component';
import { RelatoriosAbasComponent } from '../relatorios-abas/relatorios-abas.component';
import { ICONE_PADRAO, IconeGrupo, iconeDoGrupo } from './icones-grupo';

const INTERVALO_MS = 4_000;
const MAX_FALHAS_CONSULTA = 2;
const LIMITE_MS = 30 * 60_000;

export interface RecenteNaTela { relatorio: RelatorioAgenda; recente: RelatorioRecente; }

@Component({
  selector: 'app-relatorios-agenda',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, HeaderComponent, RelatoriosAbasComponent, RelatorioFiltrosComponent],
  templateUrl: './relatorios-agenda.component.html',
  styleUrl: './relatorios-agenda.component.scss',
})
export class RelatoriosAgendaComponent implements OnInit, OnDestroy {
  relatorios: RelatorioAgenda[] = [];
  busca = '';
  carregando = false;
  erro = '';
  readonly skeleton = [1, 2, 3, 4];

  aberto: RelatorioAgenda | null = null;
  valoresAbertos: Record<string, string> | undefined;
  gerando = false;
  mensagem = '';
  tipoMensagem: 'erro' | 'aviso' | 'info' = 'erro';

  recentes: RelatorioRecente[] = [];
  private icones = new Map<string, IconeGrupo>();

  // Toda geração tem um número; resposta de geração superada (modal fechado, tela destruída) é descartada.
  private geracao = 0;
  // Num objeto para o teste poder espionar.
  private readonly arquivos = { ehPdf, abrirPdf, salvarBlob };
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly login: string;

  constructor(private servico: RelatoriosAgendaService, localStorage: LocalStorageService) {
    let login = '';
    try {
      login = String(localStorage.get('login') ?? '');
    } catch {
      login = '';
    }
    this.login = login;
  }

  // Estado manual das categorias; a busca só sobrepõe a exibição, sem apagar o que o usuário abriu.
  private readonly gruposAbertos = new Set<string>();

  grupoAberto(nome: string): boolean {
    return !!this.busca.trim() || this.gruposAbertos.has(nome);
  }

  alternarGrupo(nome: string): void {
    if (!this.gruposAbertos.delete(nome)) this.gruposAbertos.add(nome);
  }

  // grupos e recentesVisiveis nascem novos a cada ciclo; sem trackBy o DOM (e o foco) seria refeito.
  trackGrupo = (_: number, g: GrupoRelatorios) => g.nome;
  trackRelatorio = (_: number, r: RelatorioAgenda) => r.id;
  trackRecente = (_: number, item: RecenteNaTela) => item.recente.id;

  get grupos(): GrupoRelatorios[] {
    return agruparRelatorios(this.relatorios, this.busca);
  }

  get recentesVisiveis(): RecenteNaTela[] {
    const lista: RecenteNaTela[] = [];
    for (const recente of this.recentes) {
      const relatorio = this.relatorios.find(r => r.id === recente.id);
      if (relatorio) lista.push({ relatorio, recente });
    }
    return lista;
  }

  ngOnInit(): void {
    this.recentes = this.lerRecentes();
    this.carregar();
  }

  ngOnDestroy(): void {
    this.geracao++;
    if (this.timer) clearTimeout(this.timer);
  }

  async carregar(): Promise<void> {
    this.carregando = true;
    this.erro = '';
    try {
      this.relatorios = await this.servico.lista();
    } catch (e: any) {
      this.relatorios = [];
      this.erro = e?.message ?? 'Não foi possível carregar os relatórios agora.';
    } finally {
      this.icones = new Map(this.relatorios.map(r => [r.id, iconeDoGrupo(r.group)]));
      this.carregando = false;
    }
  }

  formatos(r: RelatorioAgenda): string[] {
    return (Array.isArray(r.format) ? r.format : [r.format]).filter(Boolean).map(f => String(f).toUpperCase());
  }

  icone(r: RelatorioAgenda): IconeGrupo {
    return this.icones.get(r.id) ?? ICONE_PADRAO;
  }

  rotuloRecente(item: RecenteNaTela): string {
    return `Gerar de novo ${item.relatorio.name} (${this.formatos(item.relatorio).join(', ')}), gerado ${this.quando(item.recente)}, com os mesmos filtros`;
  }

  quando(rec: RelatorioRecente): string {
    return rotuloQuando(rec.geradoEm, Date.now());
  }

  abrir(r: RelatorioAgenda, valores?: Record<string, string>): void {
    this.geracao++;
    this.aberto = r;
    this.valoresAbertos = valores ? { ...valores } : undefined;
    this.gerando = false;
    this.mensagem = '';
  }

  abrirRecente(item: RecenteNaTela): void {
    this.abrir(item.relatorio, item.recente.valores);
  }

  fecharModal(): void {
    this.geracao++;
    if (this.timer) clearTimeout(this.timer);
    this.aberto = null;
    this.valoresAbertos = undefined;
    this.gerando = false;
    this.mensagem = '';
  }

  async gerar(corpo: Record<string, string>): Promise<void> {
    if (this.gerando || !this.aberto) return;
    const relatorio = this.aberto;
    const minha = ++this.geracao;
    this.gerando = true;
    this.mensagem = '';
    try {
      const r = await this.servico.gerar(relatorio.id, corpo);
      if (minha !== this.geracao) return;
      if (r.tipo === 'arquivo') {
        this.entregar(r.arquivo, relatorio, corpo);
        return;
      }
      this.avisar('Gerando… pode levar alguns minutos.', 'info');
      await this.acompanhar(r.jobId, minha, relatorio, corpo);
    } catch (e: any) {
      if (minha === this.geracao) this.avisar(e?.message ?? 'Falha ao gerar o relatório.', 'erro');
    } finally {
      if (minha === this.geracao) this.gerando = false;
    }
  }

  private async acompanhar(jobId: string, minha: number, relatorio: RelatorioAgenda, corpo: Record<string, string>): Promise<void> {
    const inicio = this.agora();
    let falhas = 0;
    while (true) {
      await this.esperar(INTERVALO_MS);
      if (minha !== this.geracao) return;
      if (this.agora() - inicio > LIMITE_MS) {
        this.avisar('A geração passou do tempo esperado.', 'aviso');
        return;
      }
      let st: StatusJob;
      try {
        st = await this.servico.statusDoJob(jobId);
        falhas = 0;
      } catch (e) {
        if (minha !== this.geracao) return;
        if (++falhas > MAX_FALHAS_CONSULTA) throw e;
        continue;
      }
      if (minha !== this.geracao) return;
      if (st.status === 'done') {
        const arquivo = await this.servico.baixarJob(jobId);
        if (minha !== this.geracao) return;
        this.entregar(arquivo, relatorio, corpo);
        return;
      }
      if (st.status === 'empty') {
        this.avisar('Nenhum dado encontrado para os filtros.', 'aviso');
        return;
      }
      if (st.status === 'error') throw new Error(st.error || 'Falha ao gerar o relatório.');
    }
  }

  private entregar(a: ArquivoGerado, relatorio: RelatorioAgenda, corpo: Record<string, string>): void {
    if (this.arquivos.ehPdf(a.contentType, a.nome)) this.arquivos.abrirPdf(a.blob, a.nome);
    else this.arquivos.salvarBlob(a.blob, a.nome);
    this.lembrar(relatorio, corpo);
    this.fecharModal();
  }

  private lembrar(relatorio: RelatorioAgenda, corpo: Record<string, string>): void {
    this.recentes = registrarRecente(this.recentes, {
      id: relatorio.id,
      valores: valoresSeguros(relatorio.params ?? [], corpo),
      geradoEm: Date.now(),
    });
    if (!this.login) return;
    try {
      window.localStorage.setItem(chaveRecentes(this.login), JSON.stringify(this.recentes));
    } catch {
      // Sem armazenamento a lista vale só nesta visita.
    }
  }

  private lerRecentes(): RelatorioRecente[] {
    if (!this.login) return [];
    try {
      return lerRecentes(window.localStorage.getItem(chaveRecentes(this.login)));
    } catch {
      return [];
    }
  }

  private avisar(texto: string, tipo: 'erro' | 'aviso' | 'info'): void {
    this.mensagem = texto;
    this.tipoMensagem = tipo;
  }

  // Trocáveis no teste.
  protected agora(): number {
    return Date.now();
  }

  protected esperar(ms: number): Promise<void> {
    return new Promise(r => (this.timer = setTimeout(r, ms)));
  }
}
